import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import TextInput from '@common/components/Inputs/TextInput';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import SelectInput from '../forms/SelectInput';
import {
  AgreementType,
  ContentType,
  getAgreementAgreeKeyValues,
  getAgreementInstructionsKeyValues,
  getAgreementTitleKeyValues,
  getChangesInstructionsKey,
} from '../../models/enums/agreements';
import { selectAgreementsData } from '../../store/general/general-slice-selectors';
import { resetAgreementsRestState } from '../../store/general/general-slice';
import ListAgreement from '../../models/general/agreements/list-agreement';
import { createAgreement, patchAgreementById } from '../../store/general/general-actions';
import {
  validateAgreementLink,
  validateAgreementName,
  validateAgreementValidFrom,
} from '../../services/validation/agreement-validation';
import { DateTime } from 'luxon';
import ValidationResult from '@common/interfaces/api/validation-result';
import DateTimeInput from '../forms/DateTimeInput';
import SwitchInput from '../forms/SwitchInput';
import i18n from 'i18n';
import { getLocalizationOrNull } from '@common/i18n';

const AgreementsForm: React.FC<{
  agreement?: ListAgreement;
  open: boolean;
  agreementType: AgreementType;
  onClose: () => void;
}> = ({ open, agreement, agreementType, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: agreementsData.rest.postError,
      patchError: agreementsData.rest.patchError,
      resetPostHandler: () =>
        dispatch(resetAgreementsRestState({ postStatus: HttpFetchStatus.Idle, postError: null })),
      resetPatchHandler: () =>
        dispatch(resetAgreementsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  const [localAgreementType, setLocalAgreementType] = useState(agreementType);
  const [isAgreementEnabled, setIsAgreementEnabled] = useState(agreement?.enabled ?? true);
  const [validFrom, setValidFrom] = useState<{ value: DateTime; validation: ValidationResult }>({
    value:
      agreement?.validFrom != null
        ? DateTime.fromJSDate(new Date(agreement.validFrom)).startOf('second')
        : DateTime.fromISO(DateTime.now().toString()).toLocal().startOf('second'),
    validation: getServerValidationResultOrNull('validFrom') ?? {
      ...validateAgreementValidFrom(DateTime.fromISO(DateTime.now().startOf('second').toString())),
    },
  });
  const [previousAgreement, setPreviousAgreement] = useState<string | null>(
    agreement?.previousAgreement ?? null,
  );

  useEffect(() => {
    if (
      (agreementsData.rest.postStatus === HttpFetchStatus.Success ||
        agreementsData.rest.patchStatus === HttpFetchStatus.Success) &&
      open
    ) {
      updateResetToggle();
      onClose();
    }
  }, [
    onClose,
    open,
    agreementsData.rest.patchStatus,
    agreementsData.rest.postStatus,
    updateResetToggle,
  ]);

  useEffect(() => {
    if (agreementType !== localAgreementType) {
      setLocalAgreementType(agreementType);
      updateResetToggle();
    }
  }, [agreementType, localAgreementType, updateResetToggle]);

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    agreement?.name ?? '',
    (newName) => validateAgreementName(newName, agreementType),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const {
    value: link,
    validationResult: linkValidationResult,
    valueChangeHandler: linkChangeHandler,
  } = useValidatedInputEffect(
    agreement?.link ?? '',
    (newLink) => validateAgreementLink(newLink),
    (_) => () => {},
    getServerValidationResultOrNull('link'),
    removeServerError,
    resetToggle,
  );

  const {
    value: contentType,
    validationResult: contentTypeValidationResult,
    valueChangeHandler: contentTypeChangeHandler,
  } = useValidatedSelectEffect(
    agreement?.contentType ?? ContentType.FILE,
    (newValue) =>
      newValue.length === 0
        ? { isValid: false, error: i18n.t('validation-error-field-required') }
        : { isValid: true, error: null },
    (_) => () => {},
    getServerValidationResultOrNull('contentType'),
    removeServerError,
    resetToggle,
  );

  const {
    value: titleKey,
    validationResult: titleKeyValidationResult,
    valueChangeHandler: titleKeyChangeHandler,
  } = useValidatedSelectEffect(
    agreement?.titleKey ?? '',
    (newValue) =>
      newValue.length === 0
        ? { isValid: false, error: i18n.t('validation-error-field-required') }
        : { isValid: true, error: null },
    (_) => () => {},
    getServerValidationResultOrNull('titleKey'),
    removeServerError,
    resetToggle,
  );

  const {
    value: instructionsKey,
    validationResult: instructionsKeyValidationResult,
    valueChangeHandler: instructionsKeyChangeHandler,
  } = useValidatedSelectEffect(
    agreement?.instructionsKey ?? '',
    (newValue) =>
      newValue.length === 0 && contentType !== ContentType.SURVEY
        ? { isValid: false, error: i18n.t('validation-error-field-required') }
        : { isValid: true, error: null },
    (_) => () => {},
    contentType === ContentType.SURVEY ? null : getServerValidationResultOrNull('instructionsKey'),
    removeServerError,
    resetToggle,
  );

  const {
    value: agreementKey,
    validationResult: agreementKeyValidationResult,
    valueChangeHandler: agreementKeyChangeHandler,
  } = useValidatedSelectEffect(
    agreement?.agreementKey ?? '',
    (newValue) =>
      newValue.length === 0 && contentType !== ContentType.SURVEY
        ? { isValid: false, error: i18n.t('validation-error-field-required') }
        : { isValid: true, error: null },
    (_) => () => {},
    contentType === ContentType.SURVEY ? null : getServerValidationResultOrNull('agreementKey'),
    removeServerError,
    resetToggle,
  );

  const validFromChangeHandler = (newValidFrom: DateTime | null) => {
    if (newValidFrom) {
      setValidFrom({ value: newValidFrom, validation: validateAgreementValidFrom(newValidFrom) });
      removeServerError('validFrom');
    }
  };

  const changeAgreementEnabledHandler = () => {
    setIsAgreementEnabled((prevState) => !prevState);
  };

  const closeHandler = () => {
    dispatch(
      resetAgreementsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
      }),
    );
    updateResetToggle();
    onClose();
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    if (agreement?.id != null) {
      dispatch(
        patchAgreementById({
          id: agreement.id,
          data: {
            name,
            enabled: isAgreementEnabled,
            validFrom: validFrom.value.toUTC().toISO()!,
            order:
              isAgreementEnabled && !agreement.enabled
                ? (agreementsData.data ?? []).filter(
                    (a) => a.agreementType === agreementType && a.enabled,
                  ).length
                : agreement.order,
            contentType: contentType as ContentType,
            titleKey,
            instructionsKey,
            agreementKey,
            link: link.length > 0 ? link : null,
            previousAgreement: previousAgreement,
          },
        }),
      );
    } else {
      dispatch(
        createAgreement({
          name,
          enabled: isAgreementEnabled,
          validFrom: validFrom.value.toUTC().toISO()!,
          order: (agreementsData.data ?? []).filter(
            (a) => a.agreementType === agreementType && a.enabled,
          ).length,
          contentType: contentType as ContentType,
          titleKey,
          instructionsKey,
          agreementKey,
          link: link.length > 0 ? link : null,
          agreementType,
          previousAgreement: previousAgreement,
        }),
      );
    }
  };

  const isFormValid =
    name.length > 0 &&
    titleKey.length > 0 &&
    contentType.length > 0 &&
    (contentType === ContentType.SURVEY ||
      (instructionsKey.length > 0 && agreementKey.length > 0)) &&
    ((contentType !== ContentType.LINK && contentType !== ContentType.SURVEY) || link.length > 0);

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={formSubmitHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {agreement?.id != null ? name : t('title-create-agreement-' + agreementType)}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={2}>
              {agreementsData.rest.postError !== null &&
                isHttpError(agreementsData.rest.postError) && (
                  <CustomAlert severity="error" header={t(agreementsData.rest.postError.message)} />
                )}
              {agreementsData.rest.patchError !== null &&
                isHttpError(agreementsData.rest.patchError) && (
                  <CustomAlert
                    severity="error"
                    header={t(agreementsData.rest.patchError.message)}
                  />
                )}
              <TextInput
                value={name}
                onChange={nameChangeHandler}
                validationResult={nameValidationResult}
                label={t('input-label-agreement-name')}
              />
              <Grid container>
                <Grid item xs={8}>
                  <DateTimeInput
                    datetime={validFrom.value}
                    title={t('input-label-agreement-valid-from') as string}
                    dateValidation={validFrom.validation}
                    onDateTimeChange={validFromChangeHandler}
                  />
                </Grid>
                <Grid item xs={4} textAlign="end">
                  <SwitchInput
                    label={t('input-label-agreement-enabled')}
                    isChecked={isAgreementEnabled}
                    onChange={changeAgreementEnabledHandler}
                  />
                </Grid>
              </Grid>
              <SelectInput
                label={t('input-label-agreement-content-type')}
                value={contentType}
                onChange={contentTypeChangeHandler}
                validationResult={contentTypeValidationResult}
              >
                {Object.values(ContentType).map((c) => (
                  <MenuItem key={c} value={c}>
                    {t('agreement-content-type-' + c)}
                  </MenuItem>
                ))}
              </SelectInput>
              <SelectInput
                label={t('input-label-agreement-title-key')}
                value={titleKey}
                onChange={titleKeyChangeHandler}
                validationResult={titleKeyValidationResult}
                renderValue={(value) => <Typography whiteSpace="wrap">{t(`${value}`)}</Typography>}
              >
                <MenuItem value="">-</MenuItem>
                {getAgreementTitleKeyValues(agreementType).map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(c)}
                  </MenuItem>
                ))}
              </SelectInput>
              <SelectInput
                label={t('input-label-agreement-instructions-key')}
                value={instructionsKey}
                onChange={instructionsKeyChangeHandler}
                validationResult={instructionsKeyValidationResult}
                renderValue={(value) => <Typography whiteSpace="wrap">{t(`${value}`)}</Typography>}
              >
                <MenuItem value="">-</MenuItem>
                {getAgreementInstructionsKeyValues(agreementType).map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(c)}
                  </MenuItem>
                ))}
              </SelectInput>
              <SelectInput
                label={t('input-label-agreement-agreement-key')}
                value={agreementKey}
                onChange={agreementKeyChangeHandler}
                validationResult={agreementKeyValidationResult}
                renderValue={(value) => <Typography whiteSpace="wrap">{t(`${value}`)}</Typography>}
              >
                <MenuItem value="">-</MenuItem>
                {getAgreementAgreeKeyValues(agreementType).map((c) => (
                  <MenuItem key={c} value={c} sx={{ whiteSpace: 'normal' }}>
                    {t(c)}
                  </MenuItem>
                ))}
              </SelectInput>
              <SelectInput
                label={t('input-label-agreement-previous-agreement')}
                value={previousAgreement ?? ''}
                onChange={(e) => setPreviousAgreement((e.target.value as string) || null)}
              >
                <MenuItem value="">-</MenuItem>
                {(agreementsData.data ?? [])
                  .filter(
                    (a) =>
                      a.agreementType === agreementType &&
                      a.contentType === contentType &&
                      a.id !== agreement?.id,
                  )
                  .map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
              </SelectInput>
              {instructionsKey &&
                previousAgreement != null &&
                getLocalizationOrNull(getChangesInstructionsKey(instructionsKey)) && (
                  <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('input-label-agreement-changes-preview')}
                    </Typography>
                    <Typography variant="body2">
                      {t(getChangesInstructionsKey(instructionsKey))}
                    </Typography>
                  </Box>
                )}
              {(contentType === ContentType.LINK || contentType === ContentType.SURVEY) && (
                <TextInput
                  value={link}
                  onChange={linkChangeHandler}
                  validationResult={linkValidationResult}
                  label={t('input-label-agreement-link')}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              <Button
                variant="contained"
                data-testid="button-add"
                disabled={
                  !isFormValid ||
                  agreementsData.rest.postStatus === HttpFetchStatus.Loading ||
                  agreementsData.rest.patchStatus === HttpFetchStatus.Loading
                }
                type="submit"
              >
                {agreementsData.rest.postStatus === HttpFetchStatus.Loading ||
                agreementsData.rest.patchStatus === HttpFetchStatus.Loading ? (
                  <CircularProgress size={24} />
                ) : agreement?.id == null ? (
                  t('button-add-agreement-' + agreementType)
                ) : (
                  t('button-save')
                )}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      <CustomSnackbar
        open={agreementsData.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-agreement-' + agreementType)}
        onClose={closeHandler}
      />
      <CustomSnackbar
        open={agreementsData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-agreement-' + agreementType)}
        onClose={closeHandler}
      />
    </React.Fragment>
  );
};

export default AgreementsForm;
